'use client'

import { useCallback, useMemo } from 'react'
import { QueryKey, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { useClientApi } from '../useClientApi'

import type {
  TGetVesselJourneyByPortInput,
  TGetVesselJourneyByPortOutput
} from '@/api/v1/resources/vesselJourney'
import { getDefaultPort } from '@/app/config/ports'
import { sortVesselsByArrivalPriority } from '@/app/utils/select/select'

export type TOptionsQueryVesselJourneyByPort = UseQueryOptions<
  TGetVesselJourneyByPortOutput,
  unknown,
  TGetVesselJourneyByPortOutput,
  QueryKey
>

const getQueryKey = (input: TGetVesselJourneyByPortInput): QueryKey => [
  'v1',
  'vesselJourney',
  'port',
  input
]

const useQueryVesselJourneyByPort = (
  input: TGetVesselJourneyByPortInput,
  options?: TOptionsQueryVesselJourneyByPort
) => {
  const { api } = useClientApi()
  return useQuery({
    queryKey: getQueryKey(input),
    queryFn: async () => {
      return api.vesselJourney.getByPort(input)
    },
    enabled: !!(input.portId || input.unlocode),
    staleTime: 2 * 60 * 1000, // 2 minutes - data is considered fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes in background
    ...options
  })
}

export default function useVesselJourneyByPort(
  input: TGetVesselJourneyByPortInput,
  options?: TOptionsQueryVesselJourneyByPort
) {
  const queryClient = useQueryClient()
  const { api } = useClientApi()

  const queryHook = useQueryVesselJourneyByPort(input, options)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getQueryKey(input) })
  }, [queryClient, input])

  const prefetchData = useCallback(
    async (prefetchInput: TGetVesselJourneyByPortInput) => {
      await queryClient.prefetchQuery({
        queryKey: getQueryKey(prefetchInput),
        queryFn: async () => {
          return api.vesselJourney.getByPort(prefetchInput)
        },
        staleTime: 2 * 60 * 1000, // 2 minutes - data is considered fresh for 2 minutes
        gcTime: 5 * 60 * 1000 // 5 minutes - keep in cache for 5 minutes
      })
    },
    [queryClient, api]
  )

  // Determine sort order based on journey status
  // EN_ROUTE (arriving vessels) → oldest dates first
  // IN_ARRIVAL_PORT (in port vessels) → recent dates first
  const sortOldestFirst = input.journeyStatus === 'EN_ROUTE'

  const sortedVessels = useMemo(() => {
    if (!queryHook.data) return undefined
    return sortVesselsByArrivalPriority(queryHook.data, {
      priorityPortUnlocode: getDefaultPort().unlocode,
      sortOldestFirst
    })
  }, [queryHook.data, sortOldestFirst])

  return {
    ...queryHook,
    invalidate,
    prefetchData,
    vessels: queryHook.data,
    sortedVessels,
    loading: queryHook.isLoading
  }
}
